export type NewsItem = {
  slug: string
  title: string
  date: string
  dateDay: string
  dateMonth: string
  publishTime: string
  author: string
  sourceUrl: string
  cover: string
  excerpt: string
  body: string
}

const DEFAULT_COVER = '/images/image_error.png'

function fileBaseName(path: string) {
  const file = path.split(/[/\\]/).pop() || path
  return file.replace(/\.html?$/i, '')
}

function decodeEntities(value: string) {
  return value
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
}

/** Date + short hash keeps ASCII URLs stable when titles contain punctuation. */
function resolveSlug(path: string) {
  const base = fileBaseName(path)
  const dateMatch = base.match(/^(\d{4}-\d{2}-\d{2})/)
  let hash = 0
  for (let i = 0; i < base.length; i++) {
    hash = (hash * 31 + base.charCodeAt(i)) >>> 0
  }
  const short = hash.toString(36).slice(0, 6)
  return dateMatch?.[1] ? `${dateMatch[1]}-${short}` : short
}

function titleFromFilename(path: string) {
  const base = fileBaseName(path)
  return base.replace(/^\d{4}-\d{2}-\d{2}_/, '').trim() || base
}

function extractTitle(raw: string, path: string) {
  const match = raw.match(/<title[^>]*>([\s\S]*?)<\/title>/i)
  const title = match?.[1]?.replace(/\s+/g, ' ').trim()
  return title || titleFromFilename(path)
}

function extractArticleHtml(raw: string) {
  const article = raw.match(/<article[^>]*>([\s\S]*?)<\/article>/i)
  if (article?.[1]) return article[1].trim()

  const body = raw.match(/<body[^>]*>([\s\S]*?)<\/body>/i)
  return (body?.[1] || raw).trim()
}

function extractCover(html: string) {
  const match = html.match(/<img[^>]+src=["']([^"']+)["']/i)
  if (!match?.[1]) return DEFAULT_COVER
  const src = decodeEntities(match[1])
  return isWechatImageUrl(src) ? toProxiedImageUrl(src) : src
}

function extractExcerpt(html: string) {
  const text = html
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  return text.length > 120 ? `${text.slice(0, 120)}…` : text
}

function extractAuthor(html: string) {
  const match = html.match(/作者[丨|｜:：]\s*([^<\n]+)/)
  return match?.[1]?.replace(/\s+/g, ' ').trim() || ''
}

function formatDateParts(date: string) {
  const [y, m, d] = date.split('-')
  return {
    date: date || '',
    dateDay: d || '',
    dateMonth: y && m ? `${y}-${m}` : '',
  }
}

function dateFromFilename(path: string) {
  const match = fileBaseName(path).match(/^(\d{4}-\d{2}-\d{2})/)
  return match?.[1] || ''
}

function parseNewsHtml(path: string, raw: string): NewsItem {
  const body = extractArticleHtml(raw)
  const date = dateFromFilename(path)
  const { dateDay, dateMonth } = formatDateParts(date)
  const publishTime = date ? `${date} 00:00:00` : ''

  return {
    slug: resolveSlug(path),
    title: extractTitle(raw, path),
    date,
    dateDay,
    dateMonth,
    publishTime,
    author: extractAuthor(body),
    sourceUrl: '',
    cover: extractCover(body),
    excerpt: extractExcerpt(body),
    body,
  }
}

function loadAllNews(): NewsItem[] {
  const modules = import.meta.glob('../assets/news/html/*.html', {
    eager: true,
    query: '?raw',
    import: 'default',
  }) as Record<string, string>

  return Object.entries(modules)
    .map(([path, raw]) => parseNewsHtml(path, raw))
    .sort((a, b) => b.publishTime.localeCompare(a.publishTime) || b.date.localeCompare(a.date))
}

let cache: NewsItem[] | null = null

export function getAllNews(): NewsItem[] {
  if (!cache) cache = loadAllNews()
  return cache
}

export function getNewsBySlug(slug: string): NewsItem | undefined {
  const decoded = decodeURIComponent(slug)
  return getAllNews().find((item) => item.slug === decoded || item.slug === slug)
}

function isWechatImageUrl(url: string) {
  try {
    const host = new URL(decodeEntities(url)).hostname
    return host === 'mmbiz.qpic.cn' || host === 'mmbiz.qlogo.cn'
  } catch {
    return false
  }
}

function toProxiedImageUrl(url: string) {
  const decoded = decodeEntities(url.trim())
  if (!isWechatImageUrl(decoded)) return decoded
  return `/api/news-image?url=${encodeURIComponent(decoded)}`
}

/** Rewrite WeChat CDN URLs via local proxy to bypass hotlink protection. */
export function renderNewsHtml(body: string) {
  let html = body

  html = html.replace(
    /(<img\b[^>]*?\bsrc=["'])([^"']+)(["'])/gi,
    (_full, prefix: string, src: string, suffix: string) =>
      `${prefix}${toProxiedImageUrl(src)}${suffix}`,
  )

  html = html.replace(
    /url\((["']?)(https?:\/\/[^)"']+)\1\)/gi,
    (_full, quote: string, src: string) => `url(${quote}${toProxiedImageUrl(src)}${quote})`,
  )

  html = html.replace(/<img\b(?![^>]*\breferrerpolicy=)/gi, '<img referrerpolicy="no-referrer"')

  return html
}

/** @deprecated Use renderNewsHtml */
export const renderNewsMarkdown = renderNewsHtml

export function searchNews(query: string) {
  const q = query.trim().toLowerCase()
  const list = getAllNews()
  if (!q) return list
  return list.filter(
    (item) =>
      item.title.toLowerCase().includes(q) ||
      item.excerpt.toLowerCase().includes(q) ||
      item.body.toLowerCase().includes(q),
  )
}
